/**
 * Merchant patterns (TMMPDM-0112; tmm-api 0194 U2, MM-D-151): the approval queue for shared merchant
 * names learned from members' naming. Four tabs (URL `view`): Ready for review and Still short (the
 * candidates, `state=CANDIDATE` with the server's `promotable=true|false` filter), Approved and Retired.
 * The search (`q`) and the page are in the URL too; the list is server-paged.
 *
 * The composition rule (YAML at `f7c843fb`): the candidate tabs split on the server's filter
 * alone, and each tab's actions follow that filter (`VIEW_ACTIONS`); no row field decides an action.
 * The gates are detail, as sent;
 * "n of 6" is their tally, as text. Automatic promotion has no switch to show.
 *
 * Each row opens its detail (the gate checklist). Ready for review offers Approve… and Reject…, Still
 * short Reject…, Approved Retire…, from the detail or the row's menu. Until tmm-api deploys 0194 U2 the list answers a
 * bare 404: "Merchant patterns aren't available yet." with no retry.
 *
 * No member, tenant or owner identity, ever: the counts are counts.
 */
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import {
  oneOf,
  positiveInt,
  text,
  useSearchDraft,
  useUrlState,
  type UrlSchema,
} from '@/hooks/useUrlState';
import { adminApi } from '@/api/tmm/admin';
import { isActingAdminRevoked } from '@/api/errors';
import type { MerchantPatternPage, MerchantPatternRow } from '@/api/tmm/types';
import { EmptyState, ErrorState, RowActionsMenu, useNotify } from '@/components/page';
import { FilterTabs, stickyTableContainerSx } from '@/components/ui';
import { formatNumber } from '@/format';
import {
  ApprovePatternDialog,
  PatternDetailDialog,
  RejectPatternDialog,
  RetirePatternDialog,
} from './MerchantPatternDialogs';
import {
  PATTERN_VIEWS,
  VIEW_QUERY,
  VIEW_ACTIONS,
  gatesTally,
  isNotDeployed,
  kindLabel,
  notAvailable,
  patternName,
  type PatternAction,
  type PatternView,
} from './knowledge';

const PAGE_SIZE = 20;
const COLUMNS = 10;

type PatternsView = { view: PatternView; q: string; page: number };

const VIEW: UrlSchema<PatternsView> = {
  view: { default: 'ready', parse: oneOf(PATTERN_VIEWS) },
  q: { default: '', parse: text },
  page: { default: 1, parse: positiveInt },
};

const headerCellSx = { fontWeight: 600 } as const;

type Dialog =
  { kind: 'detail' | 'approve' | 'reject' | 'retire'; row: MerchantPatternRow } | { kind: 'none' };

export function MerchantPatternsTab() {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [view, setView] = useUrlState(VIEW);
  const { state, promotable } = VIEW_QUERY[view.view];
  const page = view.page - 1;
  const search = useSearchDraft(view.q, (q) => setView({ q }, { history: 'replace' }));
  const [reloadToken, setReloadToken] = useState(0);
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' });
  const requestKey = `${view.view}:${view.q}:${page}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    result: MerchantPatternPage | null;
    failure: unknown;
  }>({ requestKey: null, result: null, failure: null });

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listMerchantPatterns({
        state,
        ...(promotable === undefined ? {} : { promotable }),
        ...(view.q ? { q: view.q } : {}),
        page,
        size: PAGE_SIZE,
      })
      .then((result) => {
        if (!cancelled) setResponse({ requestKey, result, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setResponse({ requestKey, result: null, failure: failure ?? 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [state, promotable, view.q, page, requestKey]);

  const current = response.requestKey === requestKey;
  const loading = !current;
  const failure = current ? response.failure : null;
  const result = current ? response.result : null;
  const rows = result?.patterns ?? [];
  const totalPages = result?.totalPages ?? 0;
  const pageNumber = (result?.page ?? page) + 1;
  const filtered = view.q !== '' || view.page > 1;
  const close = () => setDialog({ kind: 'none' });
  const stale = () => setReloadToken((n) => n + 1);
  const done = (message: string) => {
    close();
    notify.success(message);
    setReloadToken((n) => n + 1);
  };
  const actions = VIEW_ACTIONS[view.view];
  const actionLabel = (action: PatternAction) =>
    action === 'approve'
      ? t('knowledge.approve.open', 'Approve…')
      : action === 'reject'
        ? t('knowledge.reject.open', 'Reject…')
        : t('knowledge.retire.open', 'Retire…');
  const viewLabel = (tab: PatternView) =>
    tab === 'ready'
      ? t('knowledge.view.ready', 'Ready for review')
      : tab === 'short'
        ? t('knowledge.view.short', 'Still short')
        : tab === 'approved'
          ? t('knowledge.view.approved', 'Approved')
          : t('knowledge.view.retired', 'Retired');
  const emptyText =
    view.view === 'ready'
      ? t('knowledge.empty.ready', 'No candidates are ready for review.')
      : view.view === 'short'
        ? t('knowledge.empty.short', 'No candidates are still short.')
        : view.view === 'approved'
          ? t('knowledge.empty.approved', 'No approved patterns.')
          : t('knowledge.empty.retired', 'No retired patterns.');

  if (failure !== null && isNotDeployed(failure)) {
    return <EmptyState surface title={notAvailable(t, 'patterns')} />;
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <FilterTabs
          ariaLabel={t('knowledge.view.aria', 'Merchant pattern queues')}
          active={view.view}
          onChange={(key) => {
            const next = oneOf(PATTERN_VIEWS)(key);
            if (next && next !== view.view) setView({ view: next });
          }}
          tabs={PATTERN_VIEWS.map((key) => ({ key, label: viewLabel(key) }))}
        />
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          value={search.value}
          onChange={(event) => search.onChange(event.target.value)}
          label={t('knowledge.search', 'Search patterns')}
          helperText={t('knowledge.searchHint', 'Searches the pattern and the proposed name')}
          sx={{ minWidth: 260 }}
        />
      </Stack>

      {failure ? (
        <ErrorState
          error={failure}
          message={t('knowledge.loadError', 'Could not load the merchant patterns.')}
          {...(isActingAdminRevoked(failure)
            ? {}
            : { onRetry: () => setReloadToken((n) => n + 1) })}
        />
      ) : !loading && rows.length === 0 ? (
        <EmptyState
          surface
          title={emptyText}
          {...(filtered
            ? {
                filtered: {
                  onClear: () => {
                    search.discard();
                    setView({ q: '', page: 1 });
                  },
                },
              }
            : {})}
        />
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          role="region"
          aria-label={t('knowledge.regionAria', 'Merchant patterns list')}
          aria-busy={loading ? 'true' : 'false'}
          sx={stickyTableContainerSx}
        >
          <Table size="small" stickyHeader aria-label={viewLabel(view.view)}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>{t('knowledge.col.pattern', 'Pattern')}</TableCell>
                <TableCell sx={headerCellSx}>
                  {t('knowledge.col.proposedName', 'Proposed name')}
                </TableCell>
                <TableCell sx={headerCellSx}>{t('knowledge.col.kind', 'Kind')}</TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('knowledge.col.confirmations', 'Confirmations')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('knowledge.col.edits', 'Edits')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('knowledge.col.forgets', 'Forgets')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('knowledge.col.members', 'Members')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('knowledge.col.tenants', 'Tenants')}
                </TableCell>
                <TableCell sx={headerCellSx}>{t('knowledge.col.gates', 'Gates passed')}</TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('knowledge.col.actions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('common.loading', 'Loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell
                      sx={(theme) => ({ ...theme.typography.mono, overflowWrap: 'anywhere' })}
                    >
                      <Link
                        component="button"
                        type="button"
                        onClick={() => setDialog({ kind: 'detail', row })}
                        sx={{ font: 'inherit', textAlign: 'left' }}
                      >
                        {row.pattern}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{patternName(row, t)}</TableCell>
                    <TableCell>{kindLabel(row.kind, t)}</TableCell>
                    <TableCell align="right">{formatNumber(row.confirmations, locale)}</TableCell>
                    <TableCell align="right">{formatNumber(row.edits, locale)}</TableCell>
                    <TableCell align="right">{formatNumber(row.forgets, locale)}</TableCell>
                    <TableCell align="right">{formatNumber(row.distinctMembers, locale)}</TableCell>
                    <TableCell align="right">{formatNumber(row.distinctTenants, locale)}</TableCell>
                    <TableCell>{gatesTally(row.gates, t)}</TableCell>
                    <TableCell align="right">
                      <RowActionsMenu
                        name={row.pattern}
                        items={[
                          {
                            key: 'open',
                            label: t('knowledge.detail.open', 'Open'),
                            onSelect: () => setDialog({ kind: 'detail', row }),
                          },
                          ...actions.map((action) => ({
                            key: action,
                            label: actionLabel(action),
                            destructive: action !== 'approve',
                            onSelect: () => setDialog({ kind: action, row }),
                          })),
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!failure && (loading || rows.length > 0) ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2, alignItems: 'center', justifyContent: 'flex-end' }}
        >
          <Button
            variant="outlined"
            disabled={loading || (result?.page ?? page) <= 0}
            onClick={() => setView({ page: Math.max(1, view.page - 1) })}
          >
            {t('tenants.pagination.previous', 'Previous')}
          </Button>
          <Typography variant="body2" aria-live="polite">
            {totalPages === 0
              ? null
              : t('tenants.pagination.pageOf', 'Page {{page, number}} of {{total, number}}', {
                  page: pageNumber,
                  total: totalPages,
                })}
          </Typography>
          <Button
            variant="outlined"
            disabled={loading || totalPages === 0 || (result?.page ?? page) + 1 >= totalPages}
            onClick={() => setView({ page: view.page + 1 })}
          >
            {t('tenants.pagination.next', 'Next')}
          </Button>
        </Stack>
      ) : null}

      <PatternDetailDialog
        row={dialog.kind === 'detail' ? dialog.row : null}
        actions={actions}
        onClose={close}
        onApprove={(row) => setDialog({ kind: 'approve', row })}
        onReject={(row) => setDialog({ kind: 'reject', row })}
        onRetire={(row) => setDialog({ kind: 'retire', row })}
      />
      <ApprovePatternDialog
        row={dialog.kind === 'approve' ? dialog.row : null}
        onClose={close}
        onStale={stale}
        onDone={() => done(t('knowledge.approve.done', 'Pattern approved.'))}
      />
      <RejectPatternDialog
        row={dialog.kind === 'reject' ? dialog.row : null}
        onClose={close}
        onStale={stale}
        onDone={() => done(t('knowledge.reject.done', 'Pattern rejected.'))}
      />
      <RetirePatternDialog
        row={dialog.kind === 'retire' ? dialog.row : null}
        onClose={close}
        onStale={stale}
        onDone={() => done(t('knowledge.retire.done', 'Pattern retired.'))}
      />
    </Box>
  );
}
